
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchForm } from "./profile-link/SearchForm";
import { SearchResults } from "./profile-link/SearchResults";
import { LinkedProfileCard } from "./profile-link/LinkedProfileCard";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function AdminProfileLink() {
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [currentProfile, setCurrentProfile] = useState<any | null>(null);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const { toast } = useToast();
  const auth = useAuth();
  const userId = auth?.session?.user?.id;

  // Fetch current linked profile on load
  useEffect(() => {
    const fetchLinkedProfile = async () => {
      if (!userId) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('admin_user_id', userId)
          .single();
          
        if (error && error.code !== 'PGRST116') {
          throw error;
        }
        
        if (data) {
          setCurrentProfile(data);
        }
      } catch (error) {
        console.error('Error fetching linked profile:', error);
        toast({
          title: "Error",
          description: "Failed to load your linked profile",
          variant: "destructive"
        });
      }
    };
    
    fetchLinkedProfile();
  }, [userId, toast]);

  const handleSearch = async (term: string) => {
    setIsSearching(true);
    setSearchResults([]);
    setSelectedProfile(null);
    
    try {
      // Search for profiles with matching phone number
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .ilike('phone_number', `%${term}%`);
        
      if (error) throw error;
      
      // Set the search results array
      setSearchResults(data || []);
      
    } catch (error) {
      console.error('Error searching for profiles:', error);
      toast({
        title: "Search failed",
        description: "Could not search for profiles",
        variant: "destructive"
      });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleLinkProfile = async () => {
    if (!selectedProfile || !userId) return;
    
    setIsLinking(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          admin_user_id: userId,
          role: 'admin'
        })
        .eq('id', selectedProfile.id);
        
      if (error) throw error;
      
      toast({
        title: "Profile linked",
        description: "The selected profile has been linked to your admin account",
      });
      
      setCurrentProfile(selectedProfile);
      setSearchResults([]);
      setSelectedProfile(null);
      
    } catch (error) {
      console.error('Error linking profile:', error);
      toast({
        title: "Link failed",
        description: "Could not link the selected profile",
        variant: "destructive"
      });
    } finally {
      setIsLinking(false);
    }
  };
  
  const handleUnlinkProfile = async () => {
    if (!currentProfile?.id || !userId) return;
    
    setIsUnlinking(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          admin_user_id: null,
          role: 'user'
        })
        .eq('id', currentProfile.id);
        
      if (error) throw error;
      
      toast({
        title: "Profile unlinked",
        description: "The profile has been unlinked from your admin account",
      });
      
      setCurrentProfile(null);
      setShowUnlinkDialog(false);
      
    } catch (error) {
      console.error('Error unlinking profile:', error);
      toast({
        title: "Unlink failed",
        description: "Could not unlink the profile",
        variant: "destructive"
      });
    } finally {
      setIsUnlinking(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Link</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {currentProfile ? (
          <LinkedProfileCard 
            profile={currentProfile}
            onUnlink={handleUnlinkProfile}
            showUnlinkDialog={showUnlinkDialog}
            setShowUnlinkDialog={setShowUnlinkDialog}
            isLoading={isUnlinking}
          />
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Link your admin account to your subscriber profile to receive quotes and manage your subscription.
            </p>
            
            <div className="pt-2">
              <SearchForm onSearch={handleSearch} isLoading={isSearching} />
            </div>
            
            {searchResults.length > 0 && (
              <SearchResults 
                results={searchResults} 
                selectedProfile={selectedProfile}
                onSelect={setSelectedProfile}
                onConfirm={handleLinkProfile}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
